import {createContext, use} from 'react'
import {ConnectionHandler} from 'relay-runtime'

import {IssueTimelineViewType} from '../../constants/timeline'

/**
 * Connection keys matching the @connection directives in use-timeline-items.ts.
 * Centralised here so callers don't hardcode strings when computing connection IDs.
 */
export const TIMELINE_CONNECTION_KEYS = {
  front: 'Issue__frontTimelineItems',
  back: 'Issue__backTimelineItems',
} as const

const TIMELINE_CONNECTION_FILTERS = {visibleEventsOnly: true} as const

export const TABBED_TIMELINE_CONNECTION_KEYS = {
  front: 'Issue__conversationFrontItems',
  back: 'Issue__conversationBackItems',
} as const

/**
 * Compute the front and back connection IDs for a given issue.
 */
export function buildTimelineConnectionIds(issueId: string): TimelineConnectionIds {
  return {
    front: ConnectionHandler.getConnectionID(issueId, TIMELINE_CONNECTION_KEYS.front, TIMELINE_CONNECTION_FILTERS),
    back: ConnectionHandler.getConnectionID(issueId, TIMELINE_CONNECTION_KEYS.back, TIMELINE_CONNECTION_FILTERS),
  }
}

export function buildTabbedTimelineConnectionIds(issueId: string): TimelineConnectionIds {
  return {
    front: ConnectionHandler.getConnectionID(issueId, TABBED_TIMELINE_CONNECTION_KEYS.front, {
      viewType: IssueTimelineViewType.CONVERSATION,
    }),
    back: ConnectionHandler.getConnectionID(issueId, TABBED_TIMELINE_CONNECTION_KEYS.back, {
      viewType: IssueTimelineViewType.CONVERSATION,
    }),
  }
}

export type TimelineConnectionIds = {
  front: string
  back: string
}

/**
 * Provides Relay connection IDs for the current timeline so that mutations
 * (e.g. comment deletion) can properly remove edges and update totalCount.
 *
 * Using a context avoids prop-drilling through VirtualIssueTimelinePage →
 * IssueTimelineItem → IssueComment.
 */
const TimelineConnectionContext = createContext<TimelineConnectionIds | undefined>(undefined)

export const TimelineConnectionProvider = TimelineConnectionContext.Provider

const DEFAULT_CONNECTION_IDS: TimelineConnectionIds = {front: '', back: ''}

export function useTimelineConnectionIds(): TimelineConnectionIds {
  return use(TimelineConnectionContext) ?? DEFAULT_CONNECTION_IDS
}
